import React, { useRef, useState, useMemo, useCallback } from 'react';
import { Entity } from '../types';
import { getEntityColorClass } from '../utils/fileParser';
import EntityTypeSelector from './EntityTypeSelector';

interface AnnotatedTextProps {
  text: string;
  annotations: Entity[];
  availableEntityTypes: string[];
  onChange: (annotations: Entity[]) => void;
  className?: string;
  customLabelColors?: Record<string, string>;
}

interface SelectionData {
  text: string;
  start: number;
  end: number;
  position: { top: number; left: number };
}

const AnnotatedText: React.FC<AnnotatedTextProps> = ({
  text,
  annotations,
  availableEntityTypes,
  onChange,
  className,
  customLabelColors
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const DEBUG = true;
  const [selectionData, setSelectionData] = useState<SelectionData | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const sortedAnnotations = useMemo(() => {
    return [...annotations].sort((a, b) => (a.start || 0) - (b.start || 0));
  }, [annotations]);

  const handleMouseUp = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed || !containerRef.current) return;

    const rawSelected = selection.toString();
    if (!rawSelected) return;

    const range = selection.getRangeAt(0);
    const preSelectionRange = range.cloneRange();
    preSelectionRange.selectNodeContents(containerRef.current);
    preSelectionRange.setEnd(range.startContainer, range.startOffset);
    const startRaw = preSelectionRange.toString().length;
    const endRaw = startRaw + rawSelected.length;

    // Normalize leading/trailing whitespace to avoid off-by-one highlights
    const leadingWs = (rawSelected.match(/^\s*/) || [''])[0].length;
    const trailingWs = (rawSelected.match(/\s*$/) || [''])[0].length;
    const start = startRaw + leadingWs;
    const end = endRaw - trailingWs;
    const normalizedSelected = rawSelected.substring(leadingWs, rawSelected.length - trailingWs);
    if (end <= start) return;

    const rect = range.getBoundingClientRect();
    const position = { top: rect.bottom + 5, left: rect.left };

    if (DEBUG) {
      const around = 20;
      const windowStart = Math.max(0, start - around);
      const windowEnd = Math.min(text.length, end + around);
      // eslint-disable-next-line no-console
      console.log('[Annotate] mouseUp', {
        rawSelected,
        startRaw,
        endRaw,
        leadingWs,
        trailingWs,
        normalizedStart: start,
        normalizedEnd: end,
        substring: text.substring(start, end),
        normalizedSelected,
        context: text.substring(windowStart, windowEnd)
      });
    }

    // IMPORTANT: store the user's selected string (normalized), not the DOM-sliced substring
    setSelectionData({ text: normalizedSelected, start, end, position });
    // Do not immediately clear selection to avoid stealing focus from the popup input
  }, [text]);

  const handleSelectType = useCallback((entityType: string) => {
    if (selectionData) {
      let selStart = selectionData.start;
      let selEnd = selectionData.end;
      const selected = selectionData.text;
      // Correct offsets if they do not map back to the selected text (e.g., due to DOM normalization)
      if (text.substring(selStart, selEnd) !== selected && selected.length > 0) {
        // Prefer exact match at the normalized start; otherwise search around both directions
        const windowRadius = 500;
        const windowStart = Math.max(0, selStart - windowRadius);
        const windowEnd = Math.min(text.length, selEnd + windowRadius);
        const searchWindow = text.substring(windowStart, windowEnd);
        let idx = searchWindow.indexOf(selected);
        if (idx !== -1) {
          idx = windowStart + idx;
        } else {
          // Fallback: try a case-insensitive search
          const lowerIdx = searchWindow.toLowerCase().indexOf(selected.toLowerCase());
          idx = lowerIdx === -1 ? -1 : windowStart + lowerIdx;
        }
        if (DEBUG) {
          // eslint-disable-next-line no-console
          console.log('[Annotate] correcting offsets', {
            expected: selected,
            atStartEnd: text.substring(selStart, selEnd),
            foundIdx: idx,
            windowStart,
            windowEnd
          });
        }
        if (idx !== -1) {
          selStart = idx;
          selEnd = idx + selected.length;
        }
      }
      const newEntity: Entity = {
        text: selected,
        label: entityType,
        start: selStart,
        end: selEnd
      };
      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.log('[Annotate] add entity', newEntity);
      }
      onChange([...annotations, newEntity]);
    } else if (editingIndex !== null) {
      const updated = [...annotations];
      updated[editingIndex] = { ...updated[editingIndex], label: entityType };
      onChange(updated);
      setEditingIndex(null);
    }
    setSelectionData(null);
  }, [selectionData, editingIndex, annotations, onChange]);

  const handleCancel = useCallback(() => {
    setSelectionData(null);
    setEditingIndex(null);
  }, []);

  const renderSegments = () => {
    if (sortedAnnotations.length === 0) return <span>{text}</span>;

    const segments: JSX.Element[] = [];
    let lastEnd = 0;

    sortedAnnotations.forEach((ann, idx) => {
      const start = ann.start || 0;
      const end = ann.end || start + ann.text.length;

      const originalIndex = annotations.findIndex(a => a.text === ann.text && a.label === ann.label && a.start === ann.start && a.end === ann.end);

      if (start > lastEnd) {
        segments.push(
          <span key={`text-${lastEnd}`}>{text.substring(lastEnd, start)}</span>
        );
      }

      const colorClass = getEntityColorClass(ann.label, customLabelColors);
      const isCustomColor = colorClass.includes('|') || colorClass.startsWith('#') || colorClass.startsWith('rgb');
      
      // Parse custom color format: "bg:#hex|text:#hex|border:#hex"
      let bgColor, textColor, borderColor;
      if (colorClass.includes('|')) {
        const parts = colorClass.split('|');
        bgColor = parts[0].replace('bg:', '');
        textColor = parts[1].replace('text:', '');
        borderColor = parts[2] ? parts[2].replace('border:', '') : textColor;
      } else if (isCustomColor) {
        // Legacy single color format
        bgColor = colorClass + '20';
        textColor = colorClass;
        borderColor = colorClass;
      }
      
      segments.push(
        <span
          key={`entity-${start}-${end}-${ann.label}-${idx}`}
          className={isCustomColor ? 'inline-block px-1 py-0.5 rounded cursor-pointer font-medium text-xs border-2' : `${colorClass} px-1 py-0.5 rounded cursor-pointer`}
          style={isCustomColor ? {
            backgroundColor: bgColor,
            color: textColor,
            borderColor: borderColor
          } : undefined}
          onClick={() => {
            const rect = containerRef.current?.getBoundingClientRect();
            if (rect) {
              setEditingIndex(originalIndex);
              setSelectionData({
                text: ann.text,
                start,
                end,
                position: { top: rect.bottom + 5, left: rect.left + 100 }
              });
            }
          }}
        >
          {text.substring(start, end)}
        </span>
      );

      lastEnd = end;
    });

    if (lastEnd < text.length) {
      segments.push(<span key={`text-${lastEnd}`}>{text.substring(lastEnd)}</span>);
    }

    return <>{segments}</>;
  };

  return (
    <div ref={containerRef} onMouseUp={handleMouseUp} className={`${className ? className + ' ' : ''} whitespace-pre-wrap`}>
      {renderSegments()}
      {(selectionData || editingIndex !== null) && (
        <EntityTypeSelector
          availableTypes={availableEntityTypes}
          onSelect={handleSelectType}
          onCancel={handleCancel}
          position={selectionData?.position || { top: 0, left: 0 }}
        />
      )}
    </div>
  );
};

export default AnnotatedText;


